import React, { useState } from 'react';
import { formatDate } from './helpers';

// Bleu retenu pour le tracé : proche de l'identité AYA mais suffisamment
// saturé pour ne pas « lire gris » une fois réduit à un trait de 2 px.
const TRACE = '#2E6DA4';

const MARGE = { haut: 16, droite: 16, bas: 30, gauche: 38 };
const LARGEUR = 720;
const HAUTEUR = 220;

/**
 * Courbe d'avancement dans le temps.
 *
 * Une seule série (le pourcentage de tâches terminées) : pas de légende, le
 * titre suffit à l'identifier. Le SVG est écrit à la main — cela évite d'ajouter
 * une bibliothèque de graphiques pour un unique tracé.
 */
function ProgressChart({ points, titre = 'Avancement dans le temps' }) {
  const [survol, setSurvol] = useState(null);

  const donnees = (points || [])
    .filter((p) => p && p.date)
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date));

  if (donnees.length === 0) {
    return (
      <div className="card">
        <h2>{titre}</h2>
        <div className="empty-state" style={{ padding: '24px 0' }}>
          Aucun relevé pour l'instant.
        </div>
      </div>
    );
  }

  // Un seul point ne fait pas une courbe : on le dit plutôt que de tracer une
  // ligne trompeuse. Les relevés s'accumulent un par jour.
  if (donnees.length === 1) {
    const seul = donnees[0];
    return (
      <div className="card">
        <h2>{titre}</h2>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <div style={{ fontSize: 34, fontWeight: 600, color: 'var(--text-primary)' }}>
            {seul.progress_percent}%
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            au {formatDate(seul.date)}
          </div>
        </div>
        <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 10 }}>
          La courbe se construit jour après jour : un relevé est enregistré à chaque
          consultation. Revenez demain pour voir la progression.
        </p>
      </div>
    );
  }

  const traceL = LARGEUR - MARGE.gauche - MARGE.droite;
  const traceH = HAUTEUR - MARGE.haut - MARGE.bas;

  const x = (i) => MARGE.gauche + (donnees.length === 1 ? traceL / 2 : (i / (donnees.length - 1)) * traceL);
  const y = (v) => MARGE.haut + traceH - (Math.max(0, Math.min(100, v)) / 100) * traceH;

  const ligne = donnees.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p.progress_percent).toFixed(1)}`).join(' ');
  const aire =
    `${ligne} L ${x(donnees.length - 1).toFixed(1)} ${(MARGE.haut + traceH).toFixed(1)}` +
    ` L ${x(0).toFixed(1)} ${(MARGE.haut + traceH).toFixed(1)} Z`;

  // Quelques dates seulement en abscisse, sinon les libellés se chevauchent.
  const pas = Math.max(1, Math.ceil(donnees.length / 6));
  const dernier = donnees[donnees.length - 1];

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
        <h2 style={{ margin: 0 }}>{titre}</h2>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          {donnees.length} relevés
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <svg
          viewBox={`0 0 ${LARGEUR} ${HAUTEUR}`}
          style={{ width: '100%', minWidth: 420, height: 'auto', display: 'block' }}
          onMouseLeave={() => setSurvol(null)}
          role="img"
          aria-label={`Avancement : ${dernier.progress_percent}% au ${formatDate(dernier.date)}`}
        >
          {/* Grille horizontale, volontairement discrète */}
          {[0, 25, 50, 75, 100].map((v) => (
            <g key={v}>
              <line
                x1={MARGE.gauche} x2={LARGEUR - MARGE.droite}
                y1={y(v)} y2={y(v)}
                stroke="var(--border, #E4E8EE)" strokeWidth="1"
              />
              <text
                x={MARGE.gauche - 8} y={y(v) + 4}
                textAnchor="end" fontSize="10.5" fill="var(--text-muted, #8CA0B3)"
              >
                {v}%
              </text>
            </g>
          ))}

          <path d={aire} fill={TRACE} fillOpacity="0.08" />
          <path
            d={ligne}
            fill="none"
            stroke={TRACE}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Points : anneau de surface pour rester lisibles s'ils se chevauchent */}
          {donnees.map((p, i) => (
            <circle
              key={p.date}
              cx={x(i)} cy={y(p.progress_percent)}
              r={survol === i ? 6 : 4}
              fill={TRACE}
              stroke="#FFFFFF" strokeWidth="2"
            />
          ))}

          {/* Zones de survol, plus larges que les points pour être atteignables */}
          {donnees.map((p, i) => (
            <rect
              key={`z-${p.date}`}
              x={x(i) - traceL / (donnees.length * 2) - 6}
              y={MARGE.haut}
              width={traceL / donnees.length + 12}
              height={traceH}
              fill="transparent"
              onMouseEnter={() => setSurvol(i)}
            />
          ))}

          {/* Repère vertical au survol */}
          {survol !== null && (
            <line
              x1={x(survol)} x2={x(survol)}
              y1={MARGE.haut} y2={MARGE.haut + traceH}
              stroke={TRACE} strokeWidth="1" strokeDasharray="3 3" opacity="0.5"
            />
          )}

          {/* Dates en abscisse */}
          {donnees.map((p, i) =>
            i % pas === 0 || i === donnees.length - 1 ? (
              <text
                key={`d-${p.date}`}
                x={x(i)} y={HAUTEUR - 10}
                textAnchor="middle" fontSize="10.5" fill="var(--text-muted, #8CA0B3)"
              >
                {formatDate(p.date).slice(0, 5)}
              </text>
            ) : null
          )}

          {/* Valeur du dernier point, étiquetée directement */}
          <text
            x={x(donnees.length - 1)} y={y(dernier.progress_percent) - 12}
            textAnchor="end" fontSize="12" fontWeight="600" fill="var(--text-primary, #1A2332)"
          >
            {dernier.progress_percent}%
          </text>
        </svg>
      </div>

      {survol !== null && (
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 6 }}>
          <strong>{formatDate(donnees[survol].date)}</strong> — {donnees[survol].progress_percent}%
          {donnees[survol].done != null && ` · ${donnees[survol].done}/${donnees[survol].total} tâches terminées`}
          {donnees[survol].done_tasks != null && ` · ${donnees[survol].done_tasks}/${donnees[survol].total_tasks} tâches terminées`}
          {donnees[survol].overdue > 0 && ` · ${donnees[survol].overdue} en retard`}
          {donnees[survol].overdue_tasks > 0 && ` · ${donnees[survol].overdue_tasks} en retard`}
        </div>
      )}
    </div>
  );
}

export default ProgressChart;
